import React, { useState, useEffect } from "react";
import { ArrowLeft, Upload, CircleCheck, Trash2 } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { ToastMsg } from "../../Components/ToastMsg";
import { createAdmin, updateAdminDetails } from "../../api/index";
import { Loader } from "../../Components/Loader";

const AddUser = () => {
  const location = useLocation();

  const editData = location.state || {};
  const isEditMode = location.state ? true : false;
  const navigate = useNavigate();

  // ✅ State matches API (without gender & is_active)
  const [formInput, setFormInput] = useState({
    email: editData?.email || "",
    profile: {
      first_name: editData?.name?.split(" ")[0] || "",
      last_name: editData?.name?.split(" ")[1] || "",
      phone_country_code: editData?.phone?.split(" ")[0] || "",
      phone_number: editData?.phone?.split(" ")[1] || "",
      profile_pic: editData?.photo || null,
      gender: editData?.gender || "",
    },
  });
  const [loading, setLoading] = useState(false);



  // ✅ Handle root-level input (email)
  const handleInput = (e) => {
    const { name, value } = e.target;
    setFormInput((prev) => ({
      ...prev,
      [name]: value.slice(0, 100),
    }));
  };

  // ✅ Handle profile sub-fields
  const handleProfileInput = (e) => {
    const { name, value } = e.target;
    setFormInput((prev) => ({
      ...prev,
      profile: {
        ...prev.profile,
        [name]:
          name === "phone_number"
            ? value.slice(0, 10)
            : name === "phone_country_code"
            ? value.slice(0, 3)
            : value.slice(0, 100),
      },
    }));
  };

  // ✅ Handle file upload
  const handleFileChange = (e) => {
    setFormInput((prev) => ({
      ...prev,
      profile: {
        ...prev.profile,
        profile_pic: e.target.files[0],
      },
    }));
  };

  // ✅ Submit handler
  const onSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      const res = isEditMode? await updateAdminDetails({...formInput,id:editData?.id}): await createAdmin(formInput);
      console.log("✅ User created:", res);
      setLoading(false);
      ToastMsg(res?.data?.message, "success");
      // navigate back or show success toast
    } catch (err) {
      setLoading(false);
      console.error("❌ Create user failed:", err);
      ToastMsg(err?.response?.data?.message, "error");
    }
  };

  const inputClass = `block w-full rounded-md bg-[#1E1E1E] px-3 py-4 text-sm  
  placeholder-[#454545] outline-none border border-[#333] focus:border-[#F3B753]`;

  const labelClass = `absolute -top-2 left-2 inline-block rounded-lg bg-[#161616] px-1 text-xs font-medium text-[#454545]`;

  const renderFloatingInput = (
    name,
    label,
    placeholder,
    type = "text",
    isProfile = false
  ) => (
    <div className="relative mt-3">
      <label htmlFor={name} className={labelClass}>
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        placeholder={placeholder}
        className={inputClass}
        value={
          isProfile ? formInput.profile[name] || "" : formInput[name] || ""
        }
        onChange={isProfile ? handleProfileInput : handleInput}
      />
    </div>
  );

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="flex flex-col gap-4 p-8 bg-[#161616] min-h-screen">
      {/* Back Button */}
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-[#F3B753] mb-4 hover:underline"
      >
        <ArrowLeft size={18} />
        Back
      </button>

      <h2 className="text-2xl font-semibold mb-1 pb-4 border-b border-[#333]">
        {isEditMode ? "Edit User" : "Add User"}
      </h2>

      <form
        onSubmit={onSubmit}
        className="grid grid-cols-1 md:grid-cols-2 gap-6"
      >
        {renderFloatingInput(
          "first_name",
          "First Name",
          "Enter First Name",
          "text",
          true
        )}
        {renderFloatingInput(
          "last_name",
          "Last Name",
          "Enter Last Name",
          "text",
          true
        )}
        {renderFloatingInput("email", "Email ID", "Enter Email ID")}
        {renderFloatingInput(
          "phone_number",
          "Mobile Number",
          "Enter Mobile Number",
          "text",
          true
        )}
        {renderFloatingInput(
          "phone_country_code",
          "Country Code",
          "Enter Country Code",
          "text",
          true
        )}

        {/* Upload Photo */}
        <div className="flex ">
          <div className="col-span-1">
            <label className="text-sm text-[#454545] block mb-2">Gender</label>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="gender"
                  value="Male"
                  checked={formInput.profile.gender === "Male"}
                  onChange={handleProfileInput}
                  className="accent-[#F3B753]"
                />
                <span className="text-white text-sm">Male</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="gender"
                  value="Female"
                  checked={formInput.profile.gender === "Female"}
                  onChange={handleProfileInput}
                  className="accent-[#F3B753]"
                />
                <span className="text-white text-sm">Female</span>
              </label>
            </div>
          </div>
        </div>
        <div className="col-span-1">
          <label className="text-sm text-[#454545] block mb-1">
            Upload Photo
          </label>
          {formInput.profile.profile_pic ? (
            <div className="relative border border-dashed border-[#333] rounded-md bg-[#1E1E1E] px-16 py-4 flex items-center gap-4">
              <img
                src={URL.createObjectURL(formInput.profile.profile_pic)}
                alt="cover"
                className="w-[90px] h-[70px] object-cover rounded-md"
              />
              <div className="flex flex-col gap-2 text-[#454545] text-xs">
                <span className="text-[#454545] font-medium">
                  {(formInput.profile.profile_pic.size / 1024).toFixed(0)} KB
                </span>
                <span className="flex items-center font-normal">
                  <CircleCheck size={14} className="mr-1 text-green-500" />{" "}
                  Completed
                </span>
              </div>
              <button
                type="button"
                onClick={() =>
                  setFormInput((prev) => ({
                    ...prev,
                    profile: { ...prev.profile, profile_pic: null },
                  }))
                }
                className="ml-auto text-white hover:text-red-400"
              >
                <Trash2 size={18} />
              </button>
            </div>
          ) : (
            <label className="border border-dashed hover:border-[#F3B753] border-[#333] text-[#A0A0A0] bg-[#1E1E1E] rounded-md px-4 py-3 text-center text-sm hover:text-[#F3B753] flex flex-col items-center gap-2 cursor-pointer">
              <Upload size={20} />
              Upload Photo
              <p className="text-[10px] text-[#A0A0A0] mt-1">
                Only support .jpg, .png, .svg and .zip files
              </p>
              <input
                type="file"
                accept=".jpg,.png,.svg,.zip"
                className="hidden"
                onChange={handleFileChange}
              />
            </label>
          )}
        </div>

        {/* Action Buttons */}
        <div className="col-span-2 mt-8 flex gap-4 justify-end">
          <button
            type="button"
            className="border border-[#F3B753] text-[#f3b753] px-6 py-2 rounded text-sm hover:bg-[#2d2d2d]"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-[#F3B753] text-black px-6 py-2 rounded text-sm hover:bg-[#e3a840]"
          >
            Save
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddUser;
